import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
  useRef,
  useEffect,
  type JSX,
} from 'react';
import { IAnalysisSet } from '@/types/Analysis/AnalysisSets.types';
import { ISavedComparisonEntry, SavedComparisons } from '@/types/AnalysisSetComparison/SavedComparison.types';
import { useZeroDashSdk } from './ZeroDashSdkContext';

const savedComparisonsStorageKey = 'savedAnalysisSetComparisons';

// Once this many saved comparisons are stored, the ones opened longest ago
// are dropped, to keep localStorage usage bounded.
const maxSavedComparisons = 50;

interface IComparisonContext {
  selectedAnalysisSets: IAnalysisSet[];
  addAnalysisSet: (analysisSet: IAnalysisSet) => void;
  setReference: (analysisSet: IAnalysisSet) => void;
  promoteToReference: (analysisSet: IAnalysisSet) => void;
  removeAnalysisSet: (analysisSetId: string) => void;
  reorderAnalysisSets: (fromIndex: number, toIndex: number) => void;
  isAnalysisSetSelected: (analysisSetId: string) => boolean;
  isReference: (analysisSetId: string) => boolean;
  hasReference: boolean;
  clearAnalysisSets: () => void;
  savedComparisons: SavedComparisons;
  notesByAnalysisSetId: Record<string, string>;
  setNote: (analysisSetId: string, note: string) => void;
}

const ComparisonContext = createContext<IComparisonContext | undefined>(undefined);

export function useComparison(): IComparisonContext {
  const ctx = useContext(ComparisonContext);
  if (!ctx) throw new Error('Comparison context is not available at this scope');

  return ctx;
}

const loadSavedComparisons = (): SavedComparisons => {
  try {
    const raw = localStorage.getItem(savedComparisonsStorageKey);
    if (raw) return JSON.parse(raw) as SavedComparisons;
  } catch { /* ignore corrupt data */ }
  return {};
};

// Keeps only the maxSavedComparisons most recently opened comparisons.
const keepMostRecent = (comparisons: SavedComparisons): SavedComparisons => {
  const keys = Object.keys(comparisons);
  if (keys.length <= maxSavedComparisons) return comparisons;

  const keep = new Set(
    keys
      .slice()
      .sort((a, b) => comparisons[b].lastActiveAt - comparisons[a].lastActiveAt)
      .slice(0, maxSavedComparisons),
  );
  return Object.fromEntries(
    Object.entries(comparisons).filter(([key]) => keep.has(key)),
  );
};

// A comparison isn't worth persisting once it's back down to just the
// reference with nothing compared against it and no note written — that's
// not really a comparison, just a case that happened to be viewed. Trimmed
// so a whitespace-only note doesn't count as "real" content either.
const isEmptyComparison = (entries: ISavedComparisonEntry[]): boolean => (
  entries.length === 1 && entries[0].note.trim() === ''
);

export function ComparisonProvider({ children }: { children: ReactNode }): JSX.Element {
  const zeroDashSdk = useZeroDashSdk();
  const requestedIdsRef = useRef<Set<string>>(new Set());
  const currentReferenceIdRef = useRef<string | undefined>(undefined);

  const [currentReferenceId, setCurrentReferenceIdState] = useState<string | undefined>(undefined);
  const [
    savedComparisons,
    setSavedComparisons,
  ] = useState<SavedComparisons>(loadSavedComparisons);
  // Only ids and notes are persisted, so the full records live here for the
  // session: seeded from whatever the caller already had in hand, and fetched
  // for ids restored from localStorage.
  const [analysisSetsById, setAnalysisSetsById] = useState<Record<string, IAnalysisSet>>({});

  const registerAnalysisSets = useCallback((sets: IAnalysisSet[]): void => {
    if (sets.length === 0) return;
    setAnalysisSetsById((prev) => ({
      ...prev,
      ...Object.fromEntries(sets.map((s) => [s.analysisSetId, s])),
    }));
  }, []);

  const setCurrentReferenceId = useCallback((
    id: string | undefined,
    analysisSet?: IAnalysisSet,
  ): void => {
    currentReferenceIdRef.current = id;
    if (analysisSet) registerAnalysisSets([analysisSet]);
    setCurrentReferenceIdState(id);
  }, [registerAnalysisSets]);

  useEffect(() => {
    try {
      localStorage.setItem(savedComparisonsStorageKey, JSON.stringify(savedComparisons));
    } catch { /* storage full or unavailable; comparisons just won't persist */ }
  }, [savedComparisons]);

  // Memoised on a plain string so that saving a note — which writes a new
  // savedComparisons without changing which cases are compared — doesn't hand
  // out a new array and make the comparison page refetch everything.
  const currentEntryIdsKey = currentReferenceId
    ? (savedComparisons[currentReferenceId]?.entries.map((e) => e.analysisSetId).join(',')
      ?? currentReferenceId)
    : '';

  const currentEntryIds = useMemo(
    (): string[] => (currentEntryIdsKey ? currentEntryIdsKey.split(',') : []),
    [currentEntryIdsKey],
  );

  useEffect(() => {
    const missing = currentEntryIds.filter(
      (id) => !analysisSetsById[id] && !requestedIdsRef.current.has(id),
    );
    if (missing.length === 0) return;

    missing.forEach((id) => requestedIdsRef.current.add(id));
    zeroDashSdk.curation.analysisSets
      .getAnalysisSets({ analysisSetIds: missing })
      .then(registerAnalysisSets);
  }, [currentEntryIds, analysisSetsById, registerAnalysisSets, zeroDashSdk.curation.analysisSets]);

  const selectedAnalysisSets = useMemo((): IAnalysisSet[] => (
    currentEntryIds
      .map((id) => analysisSetsById[id])
      .filter((analysisSet): analysisSet is IAnalysisSet => Boolean(analysisSet))
  ), [currentEntryIds, analysisSetsById]);

  const notesByAnalysisSetId = useMemo((): Record<string, string> => {
    if (!currentReferenceId) return {};
    return Object.fromEntries(
      (savedComparisons[currentReferenceId]?.entries ?? []).map((e) => [e.analysisSetId, e.note]),
    );
  }, [savedComparisons, currentReferenceId]);

  const updateComparison = useCallback((
    refId: string,
    updater: (entries: ISavedComparisonEntry[]) => ISavedComparisonEntry[],
  ): void => {
    setSavedComparisons((prev) => {
      const existingEntries = prev[refId]?.entries ?? [];
      const nextEntries = updater(existingEntries);
      if (nextEntries === existingEntries) return prev;

      return keepMostRecent({
        ...prev,
        [refId]: { entries: nextEntries, lastActiveAt: Date.now() },
      });
    });
  }, []);

  const setNote = useCallback((analysisSetId: string, note: string): void => {
    const refId = currentReferenceIdRef.current;
    if (!refId) return;

    setSavedComparisons((prev) => {
      const existing = prev[refId];

      if (!existing) {
        if (analysisSetId !== refId || note.trim() === '') return prev;
        return keepMostRecent({
          ...prev,
          [refId]: { entries: [{ analysisSetId: refId, note }], lastActiveAt: Date.now() },
        });
      }

      const idx = existing.entries.findIndex((e) => e.analysisSetId === analysisSetId);
      if (idx === -1) return prev;
      const nextEntries = [...existing.entries];
      nextEntries[idx] = { ...nextEntries[idx], note };

      if (isEmptyComparison(nextEntries)) {
        const next = { ...prev };
        delete next[refId];
        return next;
      }

      return keepMostRecent({
        ...prev,
        [refId]: { entries: nextEntries, lastActiveAt: Date.now() },
      });
    });
  }, []);

  const addAnalysisSet = useCallback((analysisSet: IAnalysisSet): void => {
    const refId = currentReferenceIdRef.current;
    if (!refId) return;

    registerAnalysisSets([analysisSet]);
    setSavedComparisons((prev) => {
      const existing = prev[refId];

      if (!existing) {
        return keepMostRecent({
          ...prev,
          [refId]: {
            entries: [
              { analysisSetId: refId, note: '' },
              { analysisSetId: analysisSet.analysisSetId, note: '' },
            ],
            lastActiveAt: Date.now(),
          },
        });
      }

      if (existing.entries.some((e) => e.analysisSetId === analysisSet.analysisSetId)) {
        return prev;
      }
      return keepMostRecent({
        ...prev,
        [refId]: {
          entries: [...existing.entries, { analysisSetId: analysisSet.analysisSetId, note: '' }],
          lastActiveAt: Date.now(),
        },
      });
    });
  }, [registerAnalysisSets]);

  const setReference = useCallback((analysisSet: IAnalysisSet): void => {
    const newRefId = analysisSet.analysisSetId;
    if (currentReferenceIdRef.current === newRefId) return;

    setCurrentReferenceId(newRefId, analysisSet);
    setSavedComparisons((prev) => {
      if (!prev[newRefId]) return prev;
      return keepMostRecent({
        ...prev,
        [newRefId]: { ...prev[newRefId], lastActiveAt: Date.now() },
      });
    });
  }, [setCurrentReferenceId]);

  const promoteToReference = useCallback((analysisSet: IAnalysisSet): void => {
    const refId = currentReferenceIdRef.current;
    const newRefId = analysisSet.analysisSetId;
    if (!refId || refId === newRefId) return;

    setSavedComparisons((prev) => {
      const entries = prev[refId]?.entries ?? [];
      const promoted = entries.find((e) => e.analysisSetId === newRefId);
      if (!promoted) return prev;

      const reordered = [
        promoted,
        ...entries.filter((e) => e.analysisSetId !== newRefId),
      ];
      const next = { ...prev };
      delete next[refId];
      next[newRefId] = { entries: reordered, lastActiveAt: Date.now() };
      return keepMostRecent(next);
    });
    setCurrentReferenceId(newRefId, analysisSet);
  }, [setCurrentReferenceId]);

  // The reference (index 0) cannot be removed via this API — use clearAnalysisSets
  // to start a fresh comparison or click Compare on a different case.
  // Deletes the saved comparison entirely if removal leaves just the
  // reference with no note — see isEmptyComparison.
  const removeAnalysisSet = useCallback((analysisSetId: string): void => {
    const refId = currentReferenceIdRef.current;
    if (!refId) return;

    setSavedComparisons((prev) => {
      const existing = prev[refId];
      if (!existing) return prev;
      const idx = existing.entries.findIndex((e) => e.analysisSetId === analysisSetId);
      if (idx <= 0) return prev;

      const nextEntries = existing.entries.filter((entry, i) => i !== idx);
      if (isEmptyComparison(nextEntries)) {
        const next = { ...prev };
        delete next[refId];
        return next;
      }

      return keepMostRecent({
        ...prev,
        [refId]: { entries: nextEntries, lastActiveAt: Date.now() },
      });
    });
  }, []);

  // The reference (index 0) is locked in place and cannot be the source or
  // destination of a reorder. Compared cases (index >= 1) can be reordered
  // freely amongst themselves.
  const reorderAnalysisSets = useCallback((fromIndex: number, toIndex: number): void => {
    const refId = currentReferenceIdRef.current;
    if (!refId) return;
    updateComparison(refId, (entries) => {
      if (
        fromIndex <= 0
        || toIndex <= 0
        || fromIndex >= entries.length
        || toIndex >= entries.length
      ) return entries;
      const next = [...entries];
      const [moved] = next.splice(fromIndex, 1);
      next.splice(toIndex, 0, moved);
      return next;
    });
  }, [updateComparison]);

  const isAnalysisSetSelected = useCallback((analysisSetId: string): boolean => {
    const refId = currentReferenceIdRef.current;
    if (!refId) return false;
    if (refId === analysisSetId) return true;
    return (savedComparisons[refId]?.entries ?? []).some(
      (e) => e.analysisSetId === analysisSetId,
    );
  }, [savedComparisons]);

  const isReference = useCallback(
    (analysisSetId: string): boolean => (
      !!currentReferenceId && currentReferenceId === analysisSetId
    ),
    [currentReferenceId],
  );

  const clearAnalysisSets = useCallback((): void => {
    const refId = currentReferenceIdRef.current;
    if (refId) {
      setSavedComparisons((prev) => {
        if (!(refId in prev)) return prev;
        const next = { ...prev };
        delete next[refId];
        return next;
      });
    }
    setCurrentReferenceId(undefined, undefined);
  }, [setCurrentReferenceId]);

  const value = useMemo(() => ({
    selectedAnalysisSets,
    addAnalysisSet,
    setReference,
    promoteToReference,
    removeAnalysisSet,
    reorderAnalysisSets,
    isAnalysisSetSelected,
    isReference,
    hasReference: !!currentReferenceId,
    clearAnalysisSets,
    savedComparisons,
    notesByAnalysisSetId,
    setNote,
  }), [
    selectedAnalysisSets,
    addAnalysisSet,
    setReference,
    promoteToReference,
    removeAnalysisSet,
    reorderAnalysisSets,
    isAnalysisSetSelected,
    isReference,
    currentReferenceId,
    savedComparisons,
    clearAnalysisSets,
    notesByAnalysisSetId,
    setNote,
  ]);

  return (
    <ComparisonContext.Provider value={value}>
      {children}
    </ComparisonContext.Provider>
  );
}
